import * as React from 'react'

import { Field, FieldLabel, FieldMessage } from './field'
import { Input, type InputProps } from './input'
import { Select, type SelectProps } from './select'
import { Textarea, type TextareaProps } from './textarea'

interface FieldControlProps {
  readonly errorMessage?: React.ReactNode
  readonly fieldClassName?: string
  readonly helperText?: React.ReactNode
  readonly label?: React.ReactNode
  readonly labelClassName?: string
  readonly messageClassName?: string
}

export interface InputFieldProps extends InputProps, FieldControlProps {}

export const InputField = React.forwardRef<HTMLInputElement, InputFieldProps>(
  function InputField({
    'aria-describedby': ariaDescribedBy,
    'aria-invalid': ariaInvalid,
    className,
    errorMessage,
    fieldClassName,
    helperText,
    id,
    label,
    labelClassName,
    messageClassName,
    required = false,
    ...props
  }, ref) {
    const generatedId = React.useId()
    const controlId = id ?? generatedId
    const messageId = `${controlId}-message`
    const message = errorMessage ?? helperText
    const describedBy = [ariaDescribedBy, message ? messageId : undefined]
      .filter(Boolean)
      .join(' ') || undefined
    const invalid = Boolean(errorMessage) || ariaInvalid === true || ariaInvalid === 'true'

    return (
      <Field className={fieldClassName} invalid={invalid}>
        {label ? (
          <FieldLabel className={labelClassName} htmlFor={controlId} required={required}>
            {label}
          </FieldLabel>
        ) : null}
        <Input
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          className={className}
          id={controlId}
          ref={ref}
          required={required}
          {...props}
        />
        {message ? (
          <FieldMessage
            className={messageClassName}
            id={messageId}
            tone={errorMessage ? 'error' : 'default'}
          >
            {message}
          </FieldMessage>
        ) : null}
      </Field>
    )
  },
)

export interface TextareaFieldProps extends TextareaProps, FieldControlProps {}

export const TextareaField = React.forwardRef<HTMLTextAreaElement, TextareaFieldProps>(
  function TextareaField({
    'aria-describedby': ariaDescribedBy,
    'aria-invalid': ariaInvalid,
    className,
    errorMessage,
    fieldClassName,
    helperText,
    id,
    label,
    labelClassName,
    messageClassName,
    required = false,
    ...props
  }, ref) {
    const generatedId = React.useId()
    const controlId = id ?? generatedId
    const messageId = `${controlId}-message`
    const message = errorMessage ?? helperText
    const describedBy = [ariaDescribedBy, message ? messageId : undefined]
      .filter(Boolean)
      .join(' ') || undefined
    const invalid = Boolean(errorMessage) || ariaInvalid === true || ariaInvalid === 'true'

    return (
      <Field className={fieldClassName} invalid={invalid}>
        {label ? (
          <FieldLabel className={labelClassName} htmlFor={controlId} required={required}>
            {label}
          </FieldLabel>
        ) : null}
        <Textarea
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          className={className}
          id={controlId}
          ref={ref}
          required={required}
          {...props}
        />
        {message ? (
          <FieldMessage
            className={messageClassName}
            id={messageId}
            tone={errorMessage ? 'error' : 'default'}
          >
            {message}
          </FieldMessage>
        ) : null}
      </Field>
    )
  },
)

export interface SelectFieldOption {
  readonly disabled?: boolean
  readonly label: React.ReactNode
  readonly value: string
}

export interface SelectFieldProps
  extends Omit<SelectProps, 'children' | 'onChange'>,
    FieldControlProps {
  readonly onChange?: React.ChangeEventHandler<HTMLSelectElement>
  readonly onValueChange?: (value: string) => void
  readonly options: readonly SelectFieldOption[]
  readonly placeholder?: React.ReactNode
}

export const SelectField = React.forwardRef<HTMLSelectElement, SelectFieldProps>(
  function SelectField({
    'aria-describedby': ariaDescribedBy,
    'aria-invalid': ariaInvalid,
    className,
    errorMessage,
    fieldClassName,
    helperText,
    id,
    label,
    labelClassName,
    messageClassName,
    onChange,
    onValueChange,
    options,
    placeholder,
    required = false,
    ...props
  }, ref) {
    const generatedId = React.useId()
    const controlId = id ?? generatedId
    const messageId = `${controlId}-message`
    const message = errorMessage ?? helperText
    const describedBy = [ariaDescribedBy, message ? messageId : undefined]
      .filter(Boolean)
      .join(' ') || undefined
    const invalid = Boolean(errorMessage) || ariaInvalid === true || ariaInvalid === 'true'

    function handleChange(event: React.ChangeEvent<HTMLSelectElement>) {
      onChange?.(event)
      onValueChange?.(event.target.value)
    }

    return (
      <Field className={fieldClassName} invalid={invalid}>
        {label ? (
          <FieldLabel className={labelClassName} htmlFor={controlId} required={required}>
            {label}
          </FieldLabel>
        ) : null}
        <Select
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          className={className}
          id={controlId}
          onChange={handleChange}
          ref={ref}
          required={required}
          {...props}
        >
          {placeholder ? (
            <option disabled value="">
              {placeholder}
            </option>
          ) : null}
          {options.map((option) => (
            <option disabled={option.disabled} key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
        {message ? (
          <FieldMessage
            className={messageClassName}
            id={messageId}
            tone={errorMessage ? 'error' : 'default'}
          >
            {message}
          </FieldMessage>
        ) : null}
      </Field>
    )
  },
)
